import React from 'react';
import Icon from '../../../components/AppIcon';

const SuccessMetricsTracker = ({ reviews }) => {
  const publishedReviews = reviews?.filter(r => r?.reviewStatus === 'published') || [];

  const totalSavings = publishedReviews?.reduce((sum, r) => sum + (r?.costSavingsKina || 0), 0);
  const avgEfficiency = publishedReviews?.length > 0
    ? publishedReviews?.reduce((sum, r) => sum + (r?.timeEfficiencyGainPercentage || 0), 0) / publishedReviews?.length
    : 0;
  const avgCompliance = publishedReviews?.length > 0
    ? publishedReviews?.reduce((sum, r) => sum + (r?.regulatoryAdherenceScore || 0), 0) / publishedReviews?.length
    : 0;

  const topReviews = [...publishedReviews]
    ?.sort((a, b) => (b?.costSavingsKina || 0) - (a?.costSavingsKina || 0))
    ?.slice(0, 5);

  return (
    <div className="space-y-6">
      {/* Aggregate Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white dark:bg-slate-800 rounded-lg shadow-sm border border-gray-200 dark:border-slate-700 p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 bg-green-100 dark:bg-green-900/30 rounded-lg flex items-center justify-center">
              <Icon name="DollarSign" size={24} className="text-green-600 dark:text-green-400" />
            </div>
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Total Cost Savings</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">K{totalSavings?.toLocaleString()}</p>
            </div>
          </div>
          <p className="text-xs text-gray-500 dark:text-gray-500">Reported by {publishedReviews?.length} clients</p>
        </div>

        <div className="bg-white dark:bg-slate-800 rounded-lg shadow-sm border border-gray-200 dark:border-slate-700 p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center">
              <Icon name="TrendingUp" size={24} className="text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Avg Efficiency Gain</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">{avgEfficiency?.toFixed(1)}%</p>
            </div>
          </div>
          <p className="text-xs text-gray-500 dark:text-gray-500">Time savings across clients</p>
        </div>

        <div className="bg-white dark:bg-slate-800 rounded-lg shadow-sm border border-gray-200 dark:border-slate-700 p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 bg-purple-100 dark:bg-purple-900/30 rounded-lg flex items-center justify-center">
              <Icon name="Shield" size={24} className="text-purple-600 dark:text-purple-400" />
            </div>
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Avg Compliance Score</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">{avgCompliance?.toFixed(1)}%</p>
            </div>
          </div>
          <p className="text-xs text-gray-500 dark:text-gray-500">Regulatory adherence</p>
        </div>
      </div>
      {/* Top Success Stories */}
      <div className="bg-white dark:bg-slate-800 rounded-lg shadow-sm border border-gray-200 dark:border-slate-700">
        <div className="p-6 border-b border-gray-200 dark:border-slate-700">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Top Success Stories</h2>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">Highest impact client testimonials</p>
        </div>
        <div className="p-6">
          {topReviews?.length === 0 ? (
            <div className="text-center py-12">
              <Icon name="Award" size={48} className="text-gray-300 dark:text-gray-600 mx-auto mb-4" />
              <p className="text-sm text-gray-600 dark:text-gray-400">No published reviews yet</p>
            </div>
          ) : (
            <div className="space-y-4">
              {topReviews?.map((review, index) => (
                <div key={review?.id} className="flex items-start gap-4 p-4 border border-gray-200 dark:border-slate-700 rounded-lg">
                  <div className="flex-shrink-0 w-8 h-8 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center">
                    <span className="text-sm font-bold text-blue-600 dark:text-blue-400">#{index + 1}</span>
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <h3 className="font-semibold text-gray-900 dark:text-white">{review?.companyName}</h3>
                      <div className="flex items-center gap-1">
                        {[...Array(5)]?.map((_, i) => (
                          <Icon key={i} name="Star" size={12} className={i < review?.starRating ? 'text-amber-400 fill-amber-400' : 'text-gray-300'} />
                        ))}
                      </div>
                    </div>
                    <p className="text-sm text-gray-700 dark:text-gray-300 mb-2 line-clamp-2">{review?.reviewText}</p>
                    <div className="flex items-center gap-4 text-xs text-gray-500 dark:text-gray-500">
                      <span className="font-semibold text-green-600 dark:text-green-400">K{(review?.costSavingsKina || 0)?.toLocaleString()} saved</span>
                      <span>{review?.timeEfficiencyGainPercentage || 0}% efficiency</span>
                      <span>{review?.regulatoryAdherenceScore || 0}% compliance</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SuccessMetricsTracker;